// GlobalHeader.tsx — top bar (W-FE-01).
//
// Owns, left to right: the brand (the way home), the repository switcher
// (inside a repository only), which family is in scope, one search-or-jump
// control, the live pill and the account menu.
// Each child is its own file so the shell layout stays scannable.

import { Menu, Search } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { JeryuLogo } from '../components/brand/JeryuLogo';
import { FamilyScopeChip } from '../components/family/FamilyScopeChip';
import { useBootstrap } from '../hooks/useBootstrap';
import { useRealtimeStore } from '../stores/realtimeStore';
import { useCommandStore } from '../stores/commandStore';
import { RepoSwitcher } from './RepoSwitcher';
import { AccountMenu } from './AccountMenu';

interface GlobalHeaderProps {
  /** Opens the off-canvas navigation. The button that calls it is shown only
   *  where the sidebar has no room (see `.app-shell__nav-button`). */
  onOpenNav?: () => void;
}

export function GlobalHeader({ onOpenNav }: GlobalHeaderProps = {}): JSX.Element {
  const openPalette = useCommandStore((s) => s.open);
  const status = useRealtimeStore((s) => s.status);
  const bootstrap = useBootstrap();
  const { pathname } = useLocation();

  const liveLabel = liveLabelFor(status);
  const onActivity = pathname === '/activity' || pathname.startsWith('/activity/');
  const liveName = liveNameFor(status);
  const platformHint = navigator.userAgent.includes('Mac') ? '⌘ K' : 'Ctrl K';

  return (
    <div className="global-header">
      {onOpenNav ? (
        <button
          type="button"
          className="app-shell__nav-button"
          onClick={onOpenNav}
          aria-label="Open navigation"
          aria-haspopup="dialog"
          data-testid="nav-drawer-button"
        >
          <Menu size={18} aria-hidden="true" />
        </button>
      ) : null}
      <Link to="/" className="global-header__brand" aria-label="JeRyu home">
        <JeryuLogo variant="header" />
      </Link>
      <RepoSwitcher />
      <FamilyScopeChip />
      <span className="global-header__spacer" aria-hidden="true" />
      <button
        type="button"
        className="global-header__cmdk"
        onClick={() => openPalette()}
        // The name starts with the words on the button (WCAG 2.5.3).
        aria-label="Search or jump to: open the command palette"
        aria-keyshortcuts="Control+K Meta+K"
        aria-haspopup="dialog"
      >
        <Search size={14} aria-hidden="true" />
        <span className="global-header__cmdk-text">Search or jump to…</span>
        <span className="global-header__cmdk-hint" aria-hidden="true">
          {platformHint}
        </span>
      </button>
      {/* The pill is the way to the live feed. The connection state is announced
          by a separate status region: a status nested in a link is not exposed.
          On Activity there is nowhere to go, so the pill is plain state. */}
      {onActivity ? (
        <span
          className={`global-header__live global-header__live--${status}`}
          title={liveName}
          data-testid="live-pill"
        >
          <span className="global-header__live-dot" aria-hidden="true" />
          <span className="global-header__live-text" aria-hidden="true">
            {liveLabel}
          </span>
        </span>
      ) : (
        <Link
          to="/activity"
          className={`global-header__live global-header__live--${status}`}
          aria-label={`${liveName}. Open Activity`}
          title={`${liveName}. Open Activity`}
          data-testid="live-pill"
        >
          <span className="global-header__live-dot" aria-hidden="true" />
          <span className="global-header__live-text" aria-hidden="true">
            {liveLabel}
          </span>
        </Link>
      )}
      <span className="sr-only" role="status" aria-atomic="true" aria-label={liveName}>
        {liveName}
      </span>
      <AccountMenu
        login={bootstrap.data?.viewer.login ?? 'Loading…'}
        displayName={bootstrap.data?.viewer.display_name ?? null}
      />
    </div>
  );
}

/** The pill in words, for a reader who cannot see a green dot. */
export function liveNameFor(status: string): string {
  switch (status) {
    case 'open':
      return 'Live updates connected';
    case 'connecting':
      return 'Live updates connecting';
    case 'reconnecting':
      return 'Live updates reconnecting';
    case 'closed':
      return 'Live updates offline: pages refresh on their own timers';
    default:
      return 'Live updates idle';
  }
}

function liveLabelFor(status: string): string {
  switch (status) {
    case 'open':
      return 'Live';
    case 'connecting':
      return 'Connecting…';
    case 'reconnecting':
      return 'Reconnecting…';
    case 'closed':
      return 'Offline';
    default:
      return 'Idle';
  }
}
